import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Landmark, RefreshCw, Send } from 'lucide-react';
import { fetchJson, formatMoney as money } from '@/lib/api';

const ESTADOS = {
  validating: 'Checking the balance',
  charging: 'Sending',
  processing: 'On its way (ACH, a few business days)',
  paid: 'Paid',
  failed: 'Failed',
  insufficient_funds: 'Not sent: not enough balance',
  balance_unavailable: 'Not sent: the bank gave no balance',
  over_limit: 'Not sent: over the limit',
};
const EN_CAMINO = ['validating', 'charging', 'processing'];
const VACIO = { to_email: '', amount: '', memo: '' };
const espera = (ms) => new Promise((r) => { setTimeout(r, ms); });

/** Por que un banco no sirve para mandar, en palabras del comerciante. */
function MotivoBanco({ b }) {
  if (b.status === 'checking_owner') {
    return <p className="text-sm text-slate-500">Stripe is getting the account holder's name from {b.bank_name || 'the bank'}…</p>;
  }
  if (b.status === 'name_mismatch') {
    return (
      <p className="text-sm text-amber-700">
        {b.bank_name} ••{b.last4} is in the name of <b>{(b.owners || []).join(', ')}</b>. It has to be in your company's name
        or the name of a person in your KYS{b.checked_names?.length ? ` (${b.checked_names.join(', ')})` : ''}.
      </p>
    );
  }
  if (b.status === 'owner_unavailable') {
    return <p className="text-sm text-amber-700">{b.bank_name || 'The bank'} did not share the account holder's name, so it can't be used. Try another bank.</p>;
  }
  return null;
}

/**
 * SelleCheck by bank: send a check to another merchant from your bank account.
 * The sender connects a bank in its own name once; each check checks the balance first.
 * The money goes to the receiving merchant's Stripe account and from there to its bank.
 */
export default function SelleCheckBankChecks({ token, canReceive }) {
  const [params, setParams] = useSearchParams();
  const [banco, setBanco] = useState(null);
  const [tabla, setTabla] = useState(null);
  const [cheques, setCheques] = useState({ sent: [], received: [] });
  const [form, setForm] = useState(VACIO);
  const [destino, setDestino] = useState(null); // { company_name, ready } o { error }
  const [ocupado, setOcupado] = useState('');

  const cargar = useCallback(async () => {
    try {
      const [b, t, c] = await Promise.all([
        fetchJson('/merchant-payments/bank', { token }),
        fetchJson('/merchant-payments/checks/fees', { token }),
        fetchJson('/merchant-payments/checks', { token }),
      ]);
      setBanco(b);
      setTabla(t);
      setCheques(c);
    } catch (e) {
      toast.error(e.message);
    }
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  // Al volver de Stripe: se verifica el titular. Stripe puede tardar unos segundos.
  useEffect(() => {
    const sid = params.get('session_id');
    if (params.get('bank') !== 'return' || !sid) return;
    let vivo = true;
    (async () => {
      setOcupado('banco');
      try {
        for (let i = 0; i < 6 && vivo; i += 1) {
          const b = await fetchJson('/merchant-payments/bank/confirm', { token, method: 'POST', body: { session_id: sid } });
          if (!vivo) return;
          setBanco(b);
          const esperando = b.status === 'checking_owner' || b.last_attempt?.status === 'checking_owner';
          if (!esperando) {
            if (b.status === 'verified' && !b.last_attempt) toast.success('Bank account verified.');
            break;
          }
          await espera(3000);
        }
      } catch (e) {
        toast.error(e.message);
      } finally {
        if (vivo) {
          setOcupado('');
          setParams({}, { replace: true });
        }
      }
    })();
    return () => { vivo = false; };
  }, [params, setParams, token]);

  const conectar = async () => {
    setOcupado('conectar');
    try {
      const d = await fetchJson('/merchant-payments/bank/connect', {
        token, method: 'POST', body: { origin_url: window.location.origin },
      });
      window.location.href = d.url;
    } catch (e) {
      toast.error(e.message);
      setOcupado('');
    }
  };

  const buscar = async () => {
    const email = form.to_email.trim();
    if (!email) { setDestino(null); return; }
    try {
      setDestino(await fetchJson(`/merchant-payments/checks/recipient?email=${encodeURIComponent(email)}`, { token }));
    } catch (e) {
      setDestino({ error: e.message });
    }
  };

  // Un cheque recien mandado puede quedar unos segundos revisando el saldo.
  const seguir = async (id) => {
    for (let i = 0; i < 5; i += 1) {
      await espera(3000);
      const c = await fetchJson(`/merchant-payments/checks/${id}/refresh`, { token, method: 'POST', body: {} });
      if (c.status !== 'validating') return c;
    }
    return null;
  };

  const mandar = async () => {
    setOcupado('mandar');
    try {
      let c = await fetchJson('/merchant-payments/checks', {
        token, method: 'POST', body: { to_email: form.to_email.trim(), amount: Number(form.amount), memo: form.memo },
      });
      if (c.status === 'validating') c = (await seguir(c.id)) || c;
      if (['processing', 'paid'].includes(c.status)) {
        toast.success(`Check sent to ${c.to}. It takes a few business days to arrive.`);
        setForm(VACIO);
        setDestino(null);
      } else if (c.status === 'validating') {
        toast.message('Still checking your balance. The list below updates when it is done.');
      } else {
        toast.error(c.failure || ESTADOS[c.status] || c.status);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOcupado('');
      cargar();
    }
  };

  const actualizar = async (c) => {
    setOcupado(`ver-${c.id}`);
    try {
      await fetchJson(`/merchant-payments/checks/${c.id}/refresh`, { token, method: 'POST', body: {} });
      await cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOcupado('');
    }
  };

  if (!banco || !tabla) return null;

  const verificado = banco.status === 'verified';
  const monto = Number(form.amount || 0);
  const montoValido = monto >= tabla.minimum_check && monto <= tabla.maximum_check;
  const listo = verificado && montoValido && form.memo.trim() && destino?.ready && !ocupado;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 space-y-5" data-testid="bank-checks">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Landmark className="w-4 h-4 text-emerald-600" /> Send a check from your bank</h3>
        <p className="text-sm text-slate-500 mt-1">
          The money comes out of your bank account and goes to the other merchant's bank, through their Stripe account.
          Your bank account has to be in your company's name or the name of a person in your KYS, and each check checks your balance first.
        </p>
      </div>

      {/* El banco */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4" data-testid="bank-card">
        {verificado ? (
          <p className="text-sm text-emerald-700">
            {banco.bank_name} ••{banco.last4} · account holder <b>{banco.matched?.owner}</b> (verified by the bank)
          </p>
        ) : banco.status === 'none' ? (
          <p className="text-sm text-slate-500">No bank account connected yet. You sign in to your bank on a Stripe page; SelleSend never sees your password.</p>
        ) : (
          <MotivoBanco b={banco} />
        )}
        {banco.last_attempt && (
          <div className="mt-2"><MotivoBanco b={banco.last_attempt} /></div>
        )}
        <Button className="mt-3" variant={verificado ? 'outline' : 'default'} onClick={conectar} disabled={!!ocupado} data-testid="connect-bank">
          {(ocupado === 'conectar' || ocupado === 'banco') && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          {verificado ? 'Use another bank account' : 'Connect your bank'}
        </Button>
      </div>

      {/* El cheque */}
      {verificado && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="new-check">
          <div>
            <Label htmlFor="ck-to">Pay to (merchant's SelleSend email)</Label>
            <Input id="ck-to" type="email" value={form.to_email} onBlur={buscar}
                   onChange={(e) => { setForm({ ...form, to_email: e.target.value }); setDestino(null); }} data-testid="check-to" />
            {destino?.error && <p className="text-xs text-red-600 mt-1">{destino.error}</p>}
            {destino && !destino.error && (
              <p className={`text-xs mt-1 ${destino.ready ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="check-recipient">
                {destino.ready ? <>Goes to <b>{destino.company_name}</b></> : `${destino.company_name || 'That merchant'} has not connected Stripe yet, so it can't receive checks.`}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="ck-amount">Amount (USD)</Label>
            <Input id="ck-amount" type="number" step="0.01" min={tabla.minimum_check} max={tabla.maximum_check} value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="check-amount" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="ck-memo">Memo</Label>
            <Input id="ck-memo" maxLength={200} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                   placeholder="Invoice 2291, two Omega Speedmaster" data-testid="check-memo" />
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              From {money(tabla.minimum_check)} to {money(tabla.maximum_check)}; up to {money(tabla.pending_cap)} on the way at once. No fee for you. {tabla.who_pays}
            </p>
            <Button onClick={mandar} disabled={!listo} data-testid="send-check">
              {ocupado === 'mandar' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send check
            </Button>
          </div>
        </div>
      )}

      {/* Mandados */}
      {cheques.sent.length > 0 && (
        <div className="overflow-x-auto" data-testid="checks-sent">
          <h4 className="text-sm font-semibold mb-2">Checks you sent</h4>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr><th className="text-left py-1">Date</th><th className="text-left">To</th><th className="text-left">Memo</th><th className="text-right">Amount</th><th className="text-left pl-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {cheques.sent.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`check-${c.id}`}>
                  <td className="py-2 whitespace-nowrap">{(c.created_at || '').slice(0, 10)}</td>
                  <td>{c.to}</td>
                  <td className="max-w-[14rem] truncate" title={c.memo}>{c.memo}</td>
                  <td className="text-right tabular-nums">{money(c.amount)}</td>
                  <td className="pl-3" title={c.failure || ''}>{ESTADOS[c.status] || c.status}{c.status === 'failed' && c.failure ? `: ${c.failure}` : ''}</td>
                  <td className="text-right">
                    {EN_CAMINO.includes(c.status) && (
                      <Button size="sm" variant="ghost" onClick={() => actualizar(c)} disabled={!!ocupado} title="Check status">
                        <RefreshCw className={`w-4 h-4 ${ocupado === `ver-${c.id}` ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recibidos */}
      <div className="overflow-x-auto" data-testid="checks-received">
        <h4 className="text-sm font-semibold mb-2">Checks you received</h4>
        {cheques.received.length === 0 ? (
          <p className="text-sm text-slate-400">
            None yet.{canReceive ? '' : ' To receive checks, connect Stripe above: the money goes to your Stripe account and then to your bank.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr><th className="text-left py-1">Date</th><th className="text-left">From</th><th className="text-left">Account holder</th><th className="text-left">Memo</th><th className="text-right">Amount</th><th className="text-right">SelleCheck fee</th><th className="text-left pl-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {cheques.received.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`check-${c.id}`}>
                  <td className="py-2 whitespace-nowrap">{(c.created_at || '').slice(0, 10)}</td>
                  <td>{c.from}</td>
                  <td>{c.account_holder}</td>
                  <td className="max-w-[14rem] truncate" title={c.memo}>{c.memo}</td>
                  <td className="text-right tabular-nums">{money(c.amount)}</td>
                  <td className="text-right tabular-nums">{money(c.fee_amount)}</td>
                  <td className="pl-3" title={c.failure || ''}>{ESTADOS[c.status] || c.status}{c.status === 'failed' && c.failure ? `: ${c.failure}` : ''}</td>
                  <td className="text-right">
                    {EN_CAMINO.includes(c.status) && (
                      <Button size="sm" variant="ghost" onClick={() => actualizar(c)} disabled={!!ocupado} title="Check status">
                        <RefreshCw className={`w-4 h-4 ${ocupado === `ver-${c.id}` ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-500 mt-2">{tabla.risk} Stripe's ACH fee also comes out of the check.</p>
      </div>
    </div>
  );
}
